import React, { useState } from 'react';

import { faPlus, faSearch, faUserShield, faUser, faXmark, faRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Title,
  Stack,
  Button,
  Group,
  TextInput,
  Table,
  Badge,
  Text,
  Pagination,
  Box,
  ActionIcon,
  Modal,
  Drawer,
  Alert
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { useAuth } from '../../contexts/AuthContext';
import { PostJSON, usePostJSON } from '../../utils/request';

interface User {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserListProps {
  title: string;
}

const UserList: React.FC<UserListProps> = ({ title }) => {
  const { user: currentUser } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [addUserOpened, { open: openAddUser, close: closeAddUser }] = useDisclosure(false);
  const [setPasswordOpened, { open: openSetPassword, close: closeSetPassword }] = useDisclosure(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form states
  const [addUserForm, setAddUserForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    isAdmin: false
  });
  const [setPasswordForm, setSetPasswordForm] = useState({
    adminPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Use the usePostJSON hook to fetch users
  const { 
    data: usersResponse, 
    error: usersError, 
    loading, 
    refetch: refetchUsers 
  } = usePostJSON<{
    success: boolean;
    users: User[];
    total: number;
    error?: string;
  }, {
    page: number;
    limit: number;
    search?: string;
  }>('/api/account/users', {
    page: currentPage,
    limit: 10,
    search: searchTerm || undefined
  }, [currentPage, searchTerm]);

  // Extract data from response
  const users = usersResponse?.success ? usersResponse.users : [];
  const totalUsers = usersResponse?.success ? usersResponse.total : 0;
  const totalPages = Math.ceil(totalUsers / 10);
  const error = usersError ? 'Failed to fetch users' : (usersResponse?.success === false ? usersResponse.error || 'Failed to fetch users' : null);


  const handleSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleAddUser = async () => {
    setSubmitting(true);
    setFormErrors({});

    try {
      // Validate form
      const errors: Record<string, string> = {};
      if (!addUserForm.email) errors.email = 'Email is required';
      if (!addUserForm.password) errors.password = 'Password is required';
      if (!addUserForm.confirmPassword) errors.confirmPassword = 'Password confirmation is required';
      if (addUserForm.password !== addUserForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
      if (!addUserForm.firstName) errors.firstName = 'First name is required';
      if (!addUserForm.lastName) errors.lastName = 'Last name is required';

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setSubmitting(false);
        return;
      }

      const response = await PostJSON<{
        success: boolean;
        user?: User;
        error?: string;
      }>('/api/account/add_user', addUserForm);

      if (response.success) {
        notifications.show({
          title: 'Success',
          message: 'User added successfully!',
          color: 'green'
        });
        closeAddUser();
        setAddUserForm({
          email: '',
          password: '',
          confirmPassword: '',
          firstName: '',
          lastName: '',
          isAdmin: false
        });
        refetchUsers();
      } else {
        setFormErrors({ general: response.error || 'Failed to add user!' });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add user!';
      setFormErrors({ general: errorMessage });
      console.error('Error adding user:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetPassword = async () => {
    setSubmitting(true);
    setFormErrors({});

    try {
      // Validate form
      const errors: Record<string, string> = {};
      if (!setPasswordForm.adminPassword) errors.adminPassword = 'Admin password is required';
      if (!setPasswordForm.newPassword) errors.newPassword = 'New password is required';
      if (!setPasswordForm.confirmPassword) errors.confirmPassword = 'Confirm password is required';
      if (setPasswordForm.newPassword !== setPasswordForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setSubmitting(false);
        return;
      }

      if (!selectedUser) {
        setFormErrors({ general: 'No user selected' });
        setSubmitting(false);
        return;
      }

      const response = await PostJSON<{
        success: boolean;
        message?: string;
        error?: string;
      }>('/api/account/admin/set_password', {
        adminPassword: setPasswordForm.adminPassword,
        targetUserId: selectedUser.id,
        newPassword: setPasswordForm.newPassword
      });

      if (response.success) {
        notifications.show({
          title: 'Success',
          message: 'Password updated successfully!',
          color: 'green'
        });
        closeSetPassword();
        setSetPasswordForm({
          adminPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setSelectedUser(null);
      } else {
        setFormErrors({ general: response.error || 'Failed to update password!' });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update password!';
      setFormErrors({ general: errorMessage });
      console.error('Error setting password:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openSetPasswordModal = (user: User) => {
    setSelectedUser(user);
    setSetPasswordForm({
      adminPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setFormErrors({});
    openSetPassword();
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    if (user.lastName) return user.lastName;
    return user.email.split('@')[0];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Box>
        <Title mb="md" order={2}>{title}</Title>
        <Alert color="red" title="Error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Title order={2}>{title}</Title>
        <Button
          leftSection={<FontAwesomeIcon icon={faPlus} />}
          variant="filled"
          onClick={openAddUser}
        >
          Add User
        </Button>
      </Group>

      <Stack gap="md">
        <Group>
          <TextInput
            leftSection={<FontAwesomeIcon icon={faSearch} />}
            placeholder="Search users... (Press Enter to search)"
            rightSection={searchInput ? (
              <ActionIcon
                color="gray"
                size="sm"
                variant="subtle"
                onClick={handleClearSearch}
              >
                <FontAwesomeIcon icon={faXmark} />
              </ActionIcon>
            ) : null}
            style={{ flex: 1 }}
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyUp={handleKeyPress}
          />
        </Group>

        <Table highlightOnHover striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center">Loading users...</Text>
                </Table.Td>
              </Table.Tr>
            ) : users.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center">No users found</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              users.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <FontAwesomeIcon 
                        color={user.isAdmin ? '#228be6' : '#868e96'} 
                        icon={user.isAdmin ? faUserShield : faUser} 
                      />
                      <Text fw={user.id === currentUser?.id ? 600 : 400}>
                        {getUserDisplayName(user)}
                        {user.id === currentUser?.id && ' (You)'}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>{user.email}</Table.Td>
                  <Table.Td>
                    <Badge color={user.isAdmin ? 'blue' : 'gray'}>
                      {user.isAdmin ? 'Admin' : 'User'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{formatDate(user.createdAt)}</Table.Td>
                  <Table.Td>
                    <Button
                      color="blue" 
                      disabled={user.id === currentUser?.id}
                      leftSection={<FontAwesomeIcon icon={faRotate} />}
                      size="xs"
                      variant="subtle"
                      onClick={() => openSetPasswordModal(user)}
                    >
                      Reset
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <Group justify="center">
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
            />
          </Group>
        )}

        <Text c="dimmed" size="sm" ta="center">
          Showing {users.length} of {totalUsers} users
          {searchTerm && ` (filtered by "${searchTerm}")`}
        </Text>
      </Stack>

      {/* Add User Drawer */}
      <Drawer
        opened={addUserOpened}
        position="right"
        size="md"
        title="Add New User"
        onClose={closeAddUser}
      >
        <Stack gap="md">
          {formErrors.general && (
            <Alert color="red" title="Error">
              {formErrors.general}
            </Alert>
          )}

          <Group grow>
            <TextInput
              required
              error={formErrors.firstName}
              label="First Name"
              placeholder="John"
              value={addUserForm.firstName}
              onChange={(e) => setAddUserForm({ ...addUserForm, firstName: e.target.value })}
            />
            <TextInput
              required
              error={formErrors.lastName}
              label="Last Name"
              placeholder="Doe"
              value={addUserForm.lastName}
              onChange={(e) => setAddUserForm({ ...addUserForm, lastName: e.target.value })}
            />
          </Group>
          
          <TextInput
            required
            error={formErrors.email}
            label="Email"
            placeholder="user@example.com"
            value={addUserForm.email}
            onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
          />
          
          <TextInput
            required
            autoComplete="new-user-password"
            error={formErrors.password}
            label="Password"
            placeholder="Enter password"
            type="password"
            value={addUserForm.password}
            onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
          />
          
          <TextInput
            required
            autoComplete="new-user-password"
            error={formErrors.confirmPassword}
            label="Confirm Password"
            placeholder="Confirm password"
            type="password"
            value={addUserForm.confirmPassword}
            onChange={(e) => setAddUserForm({ ...addUserForm, confirmPassword: e.target.value })}
          />
          
          <Group justify="space-between">
            <Button
              disabled={submitting}
              variant="outline"
              onClick={closeAddUser}
            >
              Cancel
            </Button>
            <Button
              loading={submitting}
              onClick={() => void handleAddUser()}
            >
              Add User
            </Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Set Password Modal */}
      <Modal
        opened={setPasswordOpened}
        size="md"
        title={`Set Password for ${selectedUser ? getUserDisplayName(selectedUser) : ''}`}
        onClose={closeSetPassword}
      >
        <Stack gap="md">
          {formErrors.general && (
            <Alert color="red" title="Error">
              {formErrors.general}
            </Alert>
          )}
          
          <TextInput
            required
            autoComplete="your-own-password"
            error={formErrors.adminPassword}
            label="Your Own Password"
            placeholder="Enter your password so we can verify you are you"
            type="password"
            value={setPasswordForm.adminPassword}
            onChange={(e) => setSetPasswordForm({ ...setPasswordForm, adminPassword: e.target.value })}
          />
          
          <TextInput
            required
            error={formErrors.newPassword}
            label="New Password"
            placeholder="Enter new password"
            type="password"
            value={setPasswordForm.newPassword}
            onChange={(e) => setSetPasswordForm({ ...setPasswordForm, newPassword: e.target.value })}
          />
          
          <TextInput
            required
            error={formErrors.confirmPassword}
            label="Confirm New Password"
            placeholder="Confirm new password"
            type="password"
            value={setPasswordForm.confirmPassword}
            onChange={(e) => setSetPasswordForm({ ...setPasswordForm, confirmPassword: e.target.value })}
          />
          
          <Group justify="space-between">
            <Button
              disabled={submitting}
              variant="outline"
              onClick={closeSetPassword}
            >
              Cancel
            </Button>
            <Button
              loading={submitting}
              onClick={() => void handleSetPassword()}
            >
              Set Password
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default UserList;
